import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import Navigation from "../../stories/Components/Navigation";
import { ShortcutBarItem } from "./interface";
import classes from "./shortcutsBar.module.scss";
import { getUserSelections, resetUserSelections } from "./utils";
import PreferenceDialog from "./components/PreferenceDialog";
import {
    ALL_SHORTCUTS_BAR_LINKS,
    SHORTCUTS_MODAL_TRACKING_DATA,
    SHORTCUTS_LINKS_TRACKING_DATA,
} from "./constants";
import { ChipProps } from "../../stories/Components/Chip/interface";
import { trackToggleButtonClick } from "../../actions/Tracking/utils";
import { NavigationThemes } from "../../stories/Components/Navigation/interface";
import { IconColours } from "../../stories/Foundation/Icons/interface";
import Button from "../../stories/Components/Button";
import { ButtonTypes } from "../../stories/Components/Button/interface";
import { IconNames } from "../../stories/Foundation/Icons";
import MaskScroller from "../../stories/Components/MaskScroller";

interface RootState {
    app: {
        path: string;
    };
}
const ShortcutsBar = () => {
    const [shortcutBarItems, setLinks] = useState<ShortcutBarItem[]>([]);
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const path = useSelector((state: RootState) => state.app.path);

    const closeModal = (): void => {
        setModalIsOpen(false);
    };

    const retrieveSavedUserSelections = useCallback((): void => {
        const userShortcuts: ShortcutBarItem[] = getUserSelections();
        setLinks([...userShortcuts]);
    }, []);

    const handleSave = (): void => {
        closeModal();
        // the user selections are saved in the PreferenceDialog callback
        retrieveSavedUserSelections();
    };

    const handleReset = (): void => {
        resetUserSelections();
        closeModal();
        retrieveSavedUserSelections();
    };

    const buildPreferenceItems = (): ChipProps[] => {
        return ALL_SHORTCUTS_BAR_LINKS.map((link) => {
            const item: ChipProps = {
                label: link.label,
                active: shortcutBarItems.some(
                    (item) => item.label === link.label
                ),
                disabled: false,
            };
            return item;
        });
    };

    useEffect(() => {
        retrieveSavedUserSelections();
    }, [retrieveSavedUserSelections]);

    // only render the shortcuts bar on the homepage
    if (path !== "homepage") {
        return null;
    }

    return (
        <div
            className={classes.shortcutsBarContainer}
            data-testid="shortcuts-bar"
            aria-label="Customizable menu"
        >
            <div className={classes.shortcutsBarNav}>
                <div className={classes.shortcutsBarScrollable}>
                    <MaskScroller internalMargin="40px">
                        <Navigation
                            theme={NavigationThemes.BLACKANDWHITE}
                            iconColour={IconColours.white}
                            links={shortcutBarItems}
                            featureAttribution={SHORTCUTS_LINKS_TRACKING_DATA}
                            useInternalScroll={false}
                        />
                    </MaskScroller>
                </div>
                <div className={classes.shortcutsBarCustomize}>
                    <Button
                        type={ButtonTypes.IconButton}
                        name={IconNames.Customize}
                        size="24"
                        label={"Customize"}
                        variant={"dark"}
                        onClick={() => {
                            trackToggleButtonClick(
                                SHORTCUTS_MODAL_TRACKING_DATA[
                                    "data-feature-name"
                                ],
                                false,
                                undefined,
                                SHORTCUTS_MODAL_TRACKING_DATA[
                                    "data-feature-instance"
                                ]
                            );
                            setModalIsOpen(true);
                        }}
                    />
                </div>
            </div>
            <PreferenceDialog
                title="My Shortcuts"
                preferenceItems={buildPreferenceItems()}
                isOpen={modalIsOpen}
                resetClickHandler={handleReset}
                saveClickHandler={handleSave}
                closeClickHandler={closeModal}
            />
        </div>
    );
};

export default ShortcutsBar;
